import { Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom, Observable, ReplaySubject, Subject } from 'rxjs';
import { Router } from '@angular/router';
import { Admin } from '../models/user';
import { HttpClient } from '@angular/common/http';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private tokenSubject: Subject<string> = new ReplaySubject(1);

  get token$(): Observable<string> {
    return this.tokenSubject.asObservable();
  }

  private userSubject: BehaviorSubject<Admin> = new BehaviorSubject(null);

  get user$(): Observable<Admin> {
    return this.userSubject.asObservable();
  }

  get user(): Admin {
    return this.userSubject.value;
  }

  private token: string;

  constructor(private router: Router, private http: HttpClient) {
    this.getDataFromStorage();
  }

  getDataFromStorage() {
    const token = sessionStorage.getItem('authToken') || localStorage.getItem('authToken');
    const user = JSON.parse(sessionStorage.getItem('admin') || localStorage.getItem('admin'));
    this.tokenSubject.next(token || null);
    this.token = token;
    this.userSubject.next(user);
  }

  async acceptInvitation(token: string, password: string) {
    return await lastValueFrom(this.http.post<{ admin: Admin }>(`auth/accept-invitation`, { token, password }));
  }

  async login(email: string, password: string, twoFactorCode: string, rememberMe = true) {
    if (!email || !password) {
      throw new Error('Invalid email or password');
    }

    const resp = await this.http
      .post<{ admin: Admin; authToken: string }>('auth/login', { email, password, twoFactorCode })
      .toPromise();
    this.handleLoginResponse(resp, rememberMe);
  }

  resetAdmin(newAdmin: Admin) {
    this.handleLoginResponse({ admin: newAdmin });
  }

  private handleLoginResponse(resp: { admin: Admin; authToken?: string }, rememberMe = true) {
    const { admin, authToken } = resp;

    if (rememberMe) {
      if (authToken) localStorage.setItem('authToken', authToken);
      localStorage.setItem('admin', JSON.stringify(admin));
    }
    if (authToken) sessionStorage.setItem('authToken', authToken);
    sessionStorage.setItem('admin', JSON.stringify(admin));
    if (authToken) {
      this.tokenSubject.next(authToken);
      this.token = authToken;
    }
    this.userSubject.next(admin);
  }

  async logout() {
    await this.http.post('auth/logout', {}, { responseType: 'text' }).toPromise();
    this.clearLocalStorage();
  }

  async clearLocalStorage() {
    sessionStorage.clear();
    localStorage.clear();
    this.tokenSubject.next(null);
    this.userSubject.next(null);
    this.token = null;
    await this.router.navigate(['/login']);
  }

  isLoggedIn() {
    return !!this.token;
  }

  public async verifyCaptcha(token: string) {
    // Optional -- used if there are multiple clients that implement Recaptcha
    const queryParams = { app: 'admin' };

    const { ok } = await this.http.post<{ ok: boolean }>('captcha', { token }, { params: queryParams }).toPromise();

    return ok;
  }
}
